import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {TseExportRunCardComponent} from './tse-export-run-card.component';
import {TseExportConfirmDialogComponent} from './tse-export-confirm-dialog.component';
import {TseExportHttpService} from '../_services/exports/tse-export-http.service';
import {FiscalJobStore} from '../_services/fiscal-jobs/fiscal-job.store';
import {FiscalJobItem, FiscalJobSummary} from '../_models/fiscal-jobs/fiscal-job.models';
import {defaultPeriod} from '../_models/exports/data-export.models';
import {tseExportErrorMessage} from '../_models/exports/tse-export.models';
import {CompanyTimeZoneService} from '../../../_shared/time/company-time-zone.service';
import {companyToday} from '../../../_shared/time/company-day';

/**
 * TSE-Datenexport (#776) — where an operator, or their tax adviser, produces the machine-readable record of
 * the security device's own signed log messages and downloads it.
 *
 * ### Why this screen exists beside the DSFinV-K one
 *
 * § 4 KassenSichV Satz 1 names two exports in one sentence: the cash-register data and the data of the
 * technical security device. Satz 4 permits the two interfaces to be published separately, and they are — so
 * an auditor asks for one, the other, or both, and the operator has to be able to produce each on its own.
 * Before this screen the second one was a support ticket.
 *
 * ### There is no register filter, and there will not be one
 *
 * The file is audited as a *gapless* run of the security device's signature counters, which is a property of
 * the whole device rather than of one register — narrowing it would destroy the thing the file is checked
 * against. The far end's own register filter additionally makes every other filter be ignored, so „diese
 * Kasse, im Januar" would silently become „diese Kasse, seit jeher" with a perfectly successful response.
 * Which devices a period covers is derived server-side from this location's own record of which ones it used.
 *
 * ### One location can produce several files, and that is the period being covered COMPLETELY
 *
 * A location that replaced its security device mid-period signed on two of them, so the run has two entries —
 * „Sicherheitseinrichtung (Teil 1 von 2)" and „… (Teil 2 von 2)" — and the two together are the whole period.
 * A device that was deleted still gets an entry and settles with that reason, so a period that cannot be
 * produced in full says so in a row rather than quietly containing one file fewer.
 *
 * ### It warns before it starts, and then it starts
 *
 * Producing an export occupies the security device and can keep it from signing while it runs. The dialog
 * says that plainly and the operator decides. It deliberately does NOT refuse while a cash point is open —
 * see {@link TseExportConfirmDialogComponent}, which records why.
 *
 * ### A past export is re-downloaded, never rebuilt
 *
 * The download endpoint reads the result reference the run's entry already holds. Pressing „Herunterladen" on
 * a January export next December fetches the January file. The far end does expire a produced archive after a
 * while, and when it has, the entry stops calling itself finished and offers „Erneut erstellen" instead.
 *
 * ### It stops when it leaves
 *
 * {@link FiscalJobStore} is `providedIn: 'root'`, so it outlives this screen; without an `ngOnDestroy` the
 * open run and its 15-second fallback poll would go on requesting a run nobody can see, for the lifetime of
 * the tab (Bug #783). The store counts its callers, so leaving this screen does not close the socket under
 * the DSFinV-K one.
 */
@Component({
  selector: 'legal-tse-exports',
  standalone: true,
  imports: [FormsModule, TseExportRunCardComponent, TseExportConfirmDialogComponent],
  templateUrl: './tse-exports.component.html'
})
export class TseExportsComponent implements OnInit, OnDestroy {
  private readonly http = inject(TseExportHttpService);
  private readonly store = inject(FiscalJobStore);
  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  /**
   * The period the form opens on: the previous full calendar month, on the COMPANY's calendar (#813).
   *
   * Read once, so the two fields cannot straddle midnight, and passed the zone the service already answers —
   * the company's own once it has loaded, the server default before that, never `undefined`.
   */
  private readonly initialPeriod = defaultPeriod(this.companyTimeZone.timeZone());

  /** The first day of the period, `yyyy-MM-dd`. */
  protected readonly from = signal(this.initialPeriod.from);

  /** The last day of the period, inclusive, `yyyy-MM-dd`. */
  protected readonly to = signal(this.initialPeriod.to);

  /**
   * Today in the COMPANY's calendar, so neither field can be pointed at a day that has not happened (#813).
   *
   * Derived rather than set, and never absent — {@link CompanyTimeZoneService.timeZone} answers the server's
   * own default zone until the company's has arrived, so there is a real day here from the first render. A
   * version that started at `undefined` rendered `max="undefined"`, which is not a date, so the field had no
   * upper bound at all and `FiscalTseExportService` accepts whatever it is handed.
   *
   * The zone itself matters more here than on the DSFinV-K screen: a closing made late in the evening needs
   * the CURRENT day to be requestable in the same run that covers the day before it, or an auditor
   * reconciling the two exports finds a gap in this one that was never a gap at the security device.
   */
  protected readonly today = computed(() => companyToday(this.companyTimeZone.timeZone()));

  /** True while the confirmation dialog is open. */
  protected readonly confirming = signal(false);

  /** True while a run is being created. */
  protected readonly creating = signal(false);

  /** The entry whose file is being collected, or null. */
  protected readonly downloadingItemId = signal<string | null>(null);

  /** The entry being produced again, or null. */
  protected readonly retryingItemId = signal<string | null>(null);

  /** The run whose entries are open, or null. */
  protected readonly openRunId = signal<string | null>(null);

  /** What went wrong, in German, or null. Cleared by the next action. */
  protected readonly message = signal<string | null>(null);

  /** True when the last action succeeded and the message is good news rather than a fault. */
  protected readonly messageIsSuccess = signal(false);

  /** The engine's list, narrowed to TSE exports — a DSFinV-K export (#770) is a different screen. */
  protected readonly runs = computed<FiscalJobSummary[]>(() =>
    this.store.jobs().filter(job => job.kind === 'tseExport'));

  /** The opened run with its entries, or null. */
  protected readonly openRun = computed(() => {
    const open = this.store.openJob();
    return open && open.job.id === this.openRunId() ? open : null;
  });

  /** True while the list is being read. */
  protected readonly loading = this.store.listLoading;

  /** Whether the progress connection is up. Purely informational — every path works without it. */
  protected readonly live = this.store.live;

  /**
   * Whether the period is one the server would accept.
   *
   * The one-year cap is checked here as well as server-side, because the server's refusal costs a round trip
   * and arrives after the operator has confirmed a dialog about occupying their security device. The server
   * still owns the rule: this is the same bound written where the button is, not a second decision.
   */
  protected readonly periodTooLong = computed(() => {
    const from = Date.parse(`${this.from()}T00:00:00Z`);
    const to = Date.parse(`${this.to()}T00:00:00Z`);
    if (Number.isNaN(from) || Number.isNaN(to)) return false;
    // 366 days inclusive — the server's FiscalTseExportService.MaxPeriodDays.
    return Math.round((to - from) / 86_400_000) + 1 > 366;
  });

  /**
   * Whether both fields hold something the browser accepted.
   *
   * A native `<input type="date">` reports an empty value both for an untouched field and for a date it could
   * not parse — typing an impossible calendar day such as 31.09.2026 clears the field the same way leaving it
   * blank does. So this is also false on that kind of typo, and the template tells the two cases apart from
   * {@link periodOrderValid} rather than blaming the day order for a day that does not exist.
   */
  protected readonly periodComplete = computed(() => !!this.from() && !!this.to());

  /** Whether the complete period has „Von" on or before „Bis". */
  protected readonly periodOrderValid = computed(() => this.periodComplete() && this.from() <= this.to());

  /** Whether the create button may be pressed. */
  protected readonly canCreate = computed(() =>
    !this.creating() && this.periodOrderValid() && !this.periodTooLong());

  async ngOnInit(): Promise<void> {
    // The store owns the hub. Starting it here — rather than in a constructor — keeps a screen nobody opened
    // from holding a socket, which is the arrangement GateRefreshService documents.
    this.store.start();

    // Asks for the company's own zone; `today` reads it and narrows the date bound (#813) — see the field's
    // own doc. `load()` replays a cached zone instantly on every call after the first, so this never blocks
    // the read below.
    this.companyTimeZone.load().subscribe();

    await this.store.refreshList('tseExport');
  }

  /** Releases the store when the screen goes away (Bug #783). Paired with {@link ngOnInit}'s `start()`. */
  ngOnDestroy(): void {
    this.store.stop();
  }

  /** Opens the confirmation. Nothing has happened yet at this point. */
  protected askToCreate(): void {
    if (!this.canCreate()) return;
    this.message.set(null);
    this.confirming.set(true);
  }

  /** The operator dismissed the confirmation. Nothing was started. */
  protected cancelCreate(): void {
    this.confirming.set(false);
  }

  /** Starts a run for the chosen period, and opens it. */
  protected async create(): Promise<void> {
    if (!this.canCreate()) return;

    this.creating.set(true);
    this.message.set(null);

    try {
      const created = await firstValueFrom(this.http.create({from: this.from(), to: this.to()}));

      // Read it back through the store, so the list row and the opened run come from ONE read path and cannot
      // disagree — and so the hub's pushes land on a run the store already knows.
      this.openRunId.set(created.job.id);
      await this.store.refreshList('tseExport');
      await this.store.open(created.job.id);

      this.succeed('Der Export wurde angefordert. Sobald er bereitsteht, können Sie ihn hier herunterladen.');
    } catch (error) {
      this.fail(error);
    } finally {
      this.creating.set(false);
      // Closed in `finally`, so a refused create leaves the operator looking at the page and its message
      // rather than at a dialog that has already done its job.
      this.confirming.set(false);
    }
  }

  /** Opens or closes a run. */
  protected async toggle(jobId: string): Promise<void> {
    if (this.openRunId() === jobId) {
      this.openRunId.set(null);
      this.store.close();
      return;
    }

    this.openRunId.set(jobId);
    await this.store.open(jobId);
  }

  /**
   * Collects one entry's file and hands it to the browser.
   *
   * The name comes from the server's `Content-Disposition` — it is decided by one rule there, because a file
   * name is the one piece of this feature that leaves the application.
   */
  protected async downloadItem(item: FiscalJobItem): Promise<void> {
    if (this.downloadingItemId()) return;

    this.downloadingItemId.set(item.id);
    this.message.set(null);

    try {
      const file = await firstValueFrom(this.http.download(this.openRunId()!, item.id));
      this.save(file.blob, file.fileName);
    } catch (error) {
      // The server has just recorded that the artefact is gone, so the entry is no longer „fertig" — re-read
      // it, which is what swaps „Herunterladen" for „Erneut erstellen" without a reload. Every other refusal
      // says nothing about the entry and leaves the screen as it is.
      if (this.fail(error) === 'E_FISCAL_TSE_EXPORT_DOWNLOAD_GONE') {
        const jobId = this.openRunId();
        if (jobId) await this.store.open(jobId);
      }
    } finally {
      this.downloadingItemId.set(null);
    }
  }

  /**
   * Produces one entry again, on its own.
   *
   * Never the whole run: the entries that already succeeded have files an auditor may already hold, and
   * re-running them would replace those files with new ones for no reason.
   */
  protected async retryItem(item: FiscalJobItem): Promise<void> {
    const jobId = this.openRunId();
    if (!jobId || this.retryingItemId()) return;

    this.retryingItemId.set(item.id);
    this.message.set(null);

    const accepted = await this.store.retryItem(jobId, item.id);
    this.retryingItemId.set(null);

    if (accepted) {
      this.succeed('Der Eintrag wird erneut erstellt.');
      return;
    }

    this.message.set(tseExportErrorMessage(this.store.lastError()));
    this.messageIsSuccess.set(false);
  }

  /** Saves a blob under a name, the way every other download in this app does. */
  private save(blob: Blob, fileName: string): void {
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  }

  /**
   * Records a refusal, translated into German by its code, and answers the code it found.
   *
   * The code is returned rather than only rendered because one refusal needs an action beyond a sentence —
   * see {@link downloadItem}. It stays `null` for an error with no catalogued code, which is exactly when
   * nothing but the fallback sentence is safe to do.
   */
  private fail(error: unknown): string | null {
    const body = (error as { error?: { code?: unknown } } | null)?.error;
    const code = typeof body?.code === 'string' ? body.code : null;
    this.message.set(tseExportErrorMessage(code));
    this.messageIsSuccess.set(false);
    return code;
  }

  /** Records a success sentence. */
  private succeed(text: string): void {
    this.message.set(text);
    this.messageIsSuccess.set(true);
  }
}
